import type { PlayerRoleType, TournamentProgress } from '@repo/sdk'
import { GameSettingOptions, ROLE_EMOJIS } from '@repo/sdk'
import { Crown, Lock, Play } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { JevRoutingStatus } from '@/components/jev-routing-status'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/lib/auth'
import { gameStartButtonClassName } from '@/lib/game-start-button-style'
import { useI18n } from '@/lib/i18n'
import {
  tournamentPhaseKeys,
  tournamentUrl,
  useTournamentEdition,
} from './edition'
import { TournamentParticipation } from './participation'

export function TournamentLauncher({
  editionId,
  settings,
  onMode,
}: {
  editionId?: string
  settings: Array<{ value: string, label: string }>
  onMode: (mode: 'free' | 'quick' | 'expert') => void
}) {
  const { t } = useI18n()
  const { actor } = useAuth()
  const { edition, editionQuery, viewer, lifecycle }
    = useTournamentEdition(editionId)
  const [progress, setProgress] = useState<TournamentProgress | undefined>()
  const [role, setRole] = useState<PlayerRoleType>('psychic')
  const categories = edition?.configuration.categories
  const roles = useMemo(
    () => categories?.flatMap(category => category.allowedRoles) ?? [],
    [categories],
  )
  useEffect(() => {
    if (progress?.current)
      return
    const available = roles.find(
      option =>
        !progress
        || progress.categories.some(
          item => item.allowedRoles.includes(option) && item.remaining > 0,
        ),
    )
    if (
      available
      && (!roles.includes(role)
        || progress?.categories.find(item => item.allowedRoles.includes(role))
          ?.remaining === 0)
    ) {
      setRole(available)
    }
  }, [progress, roles, role])
  const roleName = (option: PlayerRoleType) =>
    `${ROLE_EMOJIS[option as keyof typeof ROLE_EMOJIS] ?? ''} ${t(`game.${option}`)}`
  const roster = edition?.configuration.roster ?? []
  const godRoles = roster.filter(
    option =>
      option !== 'villager'
      && option !== 'werewolf'
      && option !== 'mechanical_wolf',
  )
  const villagers = roster.filter(option => option === 'villager')
  const wolves = roster.filter(
    option => option === 'werewolf' || option === 'mechanical_wolf',
  )
  const category = edition?.configuration.categories.find(
    item => !item.allowedRoles.includes('mechanical_wolf'),
  )
  const participationVisible
    = !!edition
      && !!viewer?.registration
      && !!(lifecycle?.competitionOpen || lifecycle?.ended)
  const renderSeat = (
    option: PlayerRoleType,
    index: number,
    prefix: string,
  ) => {
    const id = `tournament-${prefix}-${index}`
    return (
      <div key={id} className="flex items-center gap-1.5">
        <Checkbox id={id} checked disabled />
        <Label
          htmlFor={id}
          className="text-sm font-normal text-foreground !opacity-100"
        >
          {roleName(option)}
          {option === 'villager' || option === 'werewolf'
            ? ` #${option === 'werewolf' ? wolves.slice(0, index + 1).filter(item => item === 'werewolf').length : index + 1}`
            : ''}
        </Label>
      </div>
    )
  }
  return (
    <div
      data-testid="game-launcher"
      data-tournament-mode="true"
      className="min-h-dvh bg-section-dark px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-8 lg:grid-cols-3">
          <Card className="flex flex-col lg:col-span-1 lg:min-h-[600px]">
            <CardHeader>
              <CardTitle>
                <h1 className="flex items-center gap-2">
                  <Play className="size-5" aria-hidden="true" />
                  {t('game.gameConfiguration')}
                </h1>
              </CardTitle>
              <CardDescription>
                {t('game.gameConfigurationDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col">
              <div
                className="grid grid-cols-2 gap-2"
                data-testid="play-mode-toggle"
              >
                <Button disabled data-testid="mode-solo">
                  {t('game.soloGame')}
                </Button>
                <Button
                  disabled
                  variant="outline"
                  data-testid="mode-multiplayer"
                >
                  {t('game.multiplayer')}
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {settings.map(setting => (
                  <button
                    key={setting.value}
                    type="button"
                    disabled
                    data-testid={`mode-tab-${setting.value}`}
                    aria-pressed={setting.value
                      === GameSettingOptions.G12_PSYCHIC_MECHANICAL}
                    className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${setting.value === GameSettingOptions.G12_PSYCHIC_MECHANICAL ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted/40 text-muted-foreground opacity-60'}`}
                  >
                    {setting.value
                      !== GameSettingOptions.G12_PSYCHIC_MECHANICAL && (
                      <Lock className="size-3" aria-hidden="true" />
                    )}
                    {setting.label}
                  </button>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                <Label htmlFor="role" className="flex items-center gap-2">
                  <Crown className="size-4" aria-hidden="true" />
                  {t('game.yourRole')}
                </Label>
                <Select
                  value={progress?.current?.role ?? role}
                  onValueChange={value => setRole(value as PlayerRoleType)}
                  disabled={!!progress?.current || !edition}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder={t('game.selectYourRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(option => (
                      <SelectItem
                        key={option}
                        value={option}
                        disabled={!!progress
                          && !progress.categories.some(
                            item =>
                              item.allowedRoles.includes(option)
                              && item.remaining > 0,
                          )}
                      >
                        {roleName(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="has-sheriff"
                    checked={edition?.configuration.hasSheriff ?? true}
                    disabled
                  />
                  <Label htmlFor="has-sheriff">
                    🎖️ {t('game.sheriffToggle')}
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox id="model-configuration" checked={false} disabled />
                  <Label
                    htmlFor="model-configuration"
                    className="text-muted-foreground"
                  >
                    ⚙️ {t('game.modelConfiguration')}
                  </Label>
                </div>
                <JevRoutingStatus />
              </div>
              <div className="mt-auto space-y-4 pt-4">
                <div className="border-t border-border" />
                <div
                  role="radiogroup"
                  aria-label={t('game.billingMode')}
                  className="grid gap-3 pt-4"
                >
                  {(['free', 'quick', 'expert'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={false}
                      data-testid={`billing-mode-${mode}`}
                      onClick={() => onMode(mode)}
                      className="flex min-h-6 w-full items-start gap-3 text-start text-foreground"
                    >
                      <span
                        className="mt-0.5 size-4 shrink-0 rounded-full border border-muted-foreground/50"
                        aria-hidden="true"
                      />
                      <span className="text-sm font-medium leading-5">
                        {t(`game.${mode}Mode`)}
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    role="radio"
                    aria-checked
                    data-testid="tournament-entry"
                    className="flex min-h-6 w-full items-start gap-3 text-start text-foreground"
                  >
                    <span
                      className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-primary"
                      aria-hidden="true"
                    >
                      <span className="size-2 rounded-full bg-primary" />
                    </span>
                    <span className="text-sm font-medium leading-5">
                      {t('tournament.entry')}
                    </span>
                    {lifecycle && (
                      <span className="ms-auto text-xs text-muted-foreground">
                        {t(
                          `tournament.${tournamentPhaseKeys[lifecycle.phase]}`,
                        )}
                      </span>
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="game-pass-excluded">{t('gamePasses.excluded.tournament')}</p>
                {edition && (
                  <div className="text-xs leading-relaxed text-muted-foreground">
                    <p className="font-semibold text-foreground">
                      {edition.content.name}
                    </p>
                    <p>
                      {t('tournamentHall.categorySummary', {
                        godRoleLimit: category?.limit ?? 0,
                        mechanicalWolfLimit:
                          edition.configuration.categories.find(item =>
                            item.allowedRoles.includes('mechanical_wolf'),
                          )?.limit ?? 0,
                      })}
                    </p>
                    <p>{t('tournament.quotaIndependence')}</p>
                    <Link
                      className="inline-flex min-h-11 items-center underline underline-offset-4"
                      to={tournamentUrl(edition.id)}
                    >
                      {t('tournament.explanation')}
                    </Link>
                  </div>
                )}
                {editionQuery.isPending && (
                  <p role="status">{t('forum.loading')}</p>
                )}
                {editionQuery.isError && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      void editionQuery.refetch()
                    }}
                  >
                    {t('social.retry')}
                  </Button>
                )}
                {participationVisible && edition ? (
                  <TournamentParticipation
                    key={`${edition.id}:${actor?.id ?? ''}`}
                    edition={edition}
                    variant="launcher"
                    role={role}
                    onProgress={setProgress}
                    onRules={() => {}}
                  />
                ) : (
                  <Button asChild className={gameStartButtonClassName}>
                    <Link
                      data-testid="start-game-button"
                      to={tournamentUrl(edition?.id)}
                    >
                      {t('game.startGame')}
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="lg:col-span-2">
            <CardContent>
              <h2 className="mb-6 text-lg font-semibold">
                {t('game.roleConfiguration')}
              </h2>
              <div className="mb-6 space-y-2">
                <Label>{t('game.numberOfPlayers')}</Label>
                <div
                  role="radiogroup"
                  aria-label={t('game.numberOfPlayers')}
                  className="grid max-w-md grid-cols-2 gap-1 rounded-lg border border-primary/30 bg-muted/30 p-1"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked
                    data-testid="player-count-10"
                    className="min-h-9 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
                  >
                    {t('game.players10')}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={false}
                    disabled
                    data-testid="player-count-12"
                    className="min-h-9 rounded-md px-3 py-1.5 text-sm text-muted-foreground opacity-40"
                  >
                    {t('game.players12')}
                  </button>
                </div>
              </div>
              <section className="space-y-3">
                <Label className="text-sm font-bold">
                  {t('game.specialRoles')} ({godRoles.length})
                </Label>
                <div className="grid grid-cols-2 gap-3 ps-2 md:grid-cols-3">
                  {godRoles.map((option, index) =>
                    renderSeat(option, index, 'god'),
                  )}
                </div>
              </section>
              <section className="mt-4 space-y-3">
                <Label className="text-sm font-bold">
                  {t('game.villagers')} ({villagers.length})
                </Label>
                <div className="grid grid-cols-2 gap-3 ps-2 md:grid-cols-4">
                  {villagers.map((option, index) =>
                    renderSeat(option, index, 'villager'),
                  )}
                </div>
              </section>
              <div className="my-6 border-t border-border" />
              <section className="space-y-3">
                <Label className="text-sm font-bold">
                  {t('game.evilFaction')} ({wolves.length})
                </Label>
                <div className="grid grid-cols-2 gap-3 ps-2 md:grid-cols-3">
                  {wolves.map((option, index) =>
                    renderSeat(option, index, 'wolf'),
                  )}
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
